import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  EMPTY_RENTAL_PROFILE,
  mergeRentalProfile,
} from "../Constants/rentalProfile";
import {
  fetchRenterProfile,
  updateRenterProfileSection,
} from "../apiInstances/rentalProfileApi";
import {
  uploadImageToBlob,
  uploadImagesToBlob,
} from "../apiInstances/imageUploadApi";

const RentalProfileContext = createContext(null);

export const RentalProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(EMPTY_RENTAL_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem("Token");
    if (!token) {
      setProfile(
        mergeRentalProfile({
          personalDetails: {
            email: localStorage.getItem("email") || "",
          },
        })
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchRenterProfile();
      const email = localStorage.getItem("email") || "";
      setProfile(
        mergeRentalProfile({
          ...data,
          personalDetails: { ...data.personalDetails, email },
        })
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 404) {
        toast.error("Could not load your renter profile.");
      }
      setProfile(
        mergeRentalProfile({
          personalDetails: {
            email: localStorage.getItem("email") || "",
          },
        })
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const updateSection = useCallback(async (section, data) => {
    const token = localStorage.getItem("Token");
    if (!token) {
      toast.error("Please log in to save your renter profile.");
      return false;
    }

    setSaving(true);
    try {
      const next = await updateRenterProfileSection(section, data);
      const email = localStorage.getItem("email") || "";
      setProfile(
        mergeRentalProfile({
          ...next,
          personalDetails: { ...next.personalDetails, email },
        })
      );
      toast.success("Saved successfully.");
      return true;
    } catch (err) {
      const message =
        err?.response?.data?.message || "Could not save. Please try again.";
      toast.error(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const uploadDocument = useCallback(async (file) => {
    const token = localStorage.getItem("Token");
    if (!token) {
      toast.error("Please log in to upload files.");
      return null;
    }

    setSaving(true);
    try {
      return await uploadImageToBlob(file);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not upload file.";
      toast.error(message);
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const uploadDocuments = useCallback(async (files) => {
    const token = localStorage.getItem("Token");
    if (!token) {
      toast.error("Please log in to upload files.");
      return [];
    }
    if (!files?.length) return [];

    setSaving(true);
    try {
      return await uploadImagesToBlob(files);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not upload files.";
      toast.error(message);
      return [];
    } finally {
      setSaving(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      profile,
      loading,
      saving,
      loadProfile,
      updateSection,
      uploadDocument,
      uploadDocuments,
    }),
    [
      profile,
      loading,
      saving,
      loadProfile,
      updateSection,
      uploadDocument,
      uploadDocuments,
    ]
  );

  return (
    <RentalProfileContext.Provider value={value}>
      {children}
    </RentalProfileContext.Provider>
  );
};

export const useRentalProfile = () => {
  const ctx = useContext(RentalProfileContext);
  if (!ctx) {
    throw new Error("useRentalProfile must be used within RentalProfileProvider");
  }
  return ctx;
};
